// src/components/PoolCard.js
import React, { useState, useContext } from 'react';
import { WalletContext } from '../contexts/WalletContext';
import { ContractContext } from '../contexts/ContractContext';
import { 
  formatTimeRemaining, 
  generatePoolName, 
  generatePoolColors,
  calculatePoolPrize
} from '../utils/contractUtils';
import WinnersList from './WinnersList';

const PoolCard = ({ pool, type = 'active' }) => {
  const [entriesAmount, setEntriesAmount] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showWinners, setShowWinners] = useState(false);
  const { account } = useContext(WalletContext);
  const { enterPool, completePool } = useContext(ContractContext);

  const isCreator = pool.creator.toLowerCase() === account?.toLowerCase();
  const poolName = generatePoolName(pool.id);
  const progressPercent = (pool.totalEntries / pool.maxEntries) * 100;
  const timeRemaining = formatTimeRemaining(pool.creationTime, pool.duration);
  
  // Calculate prize percentages
  const prizePercent = 100 - 
    (parseInt(pool.creatorFeePercent) / 10) - 
    (parseInt(pool.platformFeePercent) / 10) - 
    (parseInt(pool.gasFeePercent) / 10);
  
  const estimatedPrize = calculatePoolPrize(
    pool.entryFee, 
    pool.maxEntries, 
    prizePercent
  );

  // Calculate total cost for entries
  const totalCost = (parseFloat(pool.entryFee) * entriesAmount).toFixed(3);

  const handleEnterPool = async () => {
    if (entriesAmount <= 0) {
      alert('Please choose at least 1 entry');
      return;
    }

    setIsProcessing(true);
    try {
      await enterPool(pool.id, entriesAmount);
      alert('You have successfully entered the lottery!');
    } catch (error) {
      console.error('Error entering pool:', error);
      alert('Failed to enter lottery: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCompletePool = async () => {
    setIsProcessing(true);
    try {
      await completePool(pool.id);
      alert('Pool has been completed successfully!');
    } catch (error) {
      console.error('Error completing pool:', error);
      alert('Failed to complete pool: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Get remaining entries user can purchase
  const getRemainingEntries = () => {
    if (type === 'user-entry') {
      return parseInt(pool.maxEntriesPerUser) - parseInt(pool.entriesCount);
    }
    return parseInt(pool.maxEntriesPerUser);
  };

  // Pool has expired but not completed
  const hasExpired = () => {
    if (pool.duration === "0") return false;
    
    const endTime = parseInt(pool.creationTime) + parseInt(pool.duration);
    const now = Math.floor(Date.now() / 1000);
    
    return now > endTime && !pool.completed;
  };

  return (
    <div className="card pool-card">
      <div className="pool-header">
        <h3>
          <span 
            className="pool-name"
          >
            {poolName}
          </span>
        </h3>
        <span className={`status ${pool.completed ? 'status-completed' : 'status-active'}`}>
          {pool.completed ? 'Completed' : 'Active'}
        </span>
      </div>
      
      <div className="pool-details">
        <p><strong>Entry Fee:</strong> {pool.entryFee} MONAD</p>
        <p><strong>Max per User:</strong> {pool.maxEntriesPerUser} entries</p>
        <p><strong>Total Capacity:</strong> {pool.maxEntries} entries</p>
        
        {type === 'user-entry' && (
          <p><strong>Your Entries:</strong> {pool.entriesCount}</p>
        )}
        
        <p><strong>Time Remaining:</strong> {timeRemaining}</p>
        
        <div className="progress">
          <div 
            className="progress-bar" 
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>
        
        <p>
          {pool.totalEntries}/{pool.maxEntries} entries sold
        </p>
        
        <p><strong>Estimated Prize:</strong> {estimatedPrize} MONAD</p>
        
        {/* Show completed pool actions */}
        {pool.completed && (
          <button 
            className="btn-warning" 
            style={{ marginTop: '10px' }}
            onClick={() => setShowWinners(true)}
          >
            View Winners
          </button>
        )}
        
        {/* Show complete button for creators if pool expired */}
        {isCreator && hasExpired() && !pool.completed && (
          <button 
            className="btn-success" 
            style={{ marginTop: '10px' }}
            onClick={handleCompletePool}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Complete Pool'}
          </button>
        )}
      </div>
      
      {/* Entry form for active pools */}
      {!pool.completed && (
        <div className="entry-form">
          {isCreator ? (
            <p className="alert alert-info">
              As the creator of this pool, you'll receive 5% of the total as a commission when completed, but you cannot participate.
            </p>
          ) : type === 'user-entry' && getRemainingEntries() <= 0 ? (
            <p><em>You've reached the maximum number of entries for this pool</em></p>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor={`entries-amount-${pool.id}`}>Number of entries:</label>
                <input 
                  id={`entries-amount-${pool.id}`}
                  type="number" 
                  className="entries-amount" 
                  min="1" 
                  max={getRemainingEntries()}
                  value={entriesAmount} 
                  onChange={(e) => setEntriesAmount(parseInt(e.target.value) || 1)}
                />
              </div>
              <p><strong>Total to pay:</strong> <span className="entry-total">{totalCost}</span> MONAD</p>
              <button 
                className="btn-enter-pool"
                onClick={handleEnterPool}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : (type === 'user-entry' ? 'Buy More Entries' : 'Enter Pool')}
              </button>
            </>
          )}
        </div>
      )}
      
      {/* Winners modal */}
      {showWinners && (
        <WinnersList 
          poolId={pool.id} 
          poolName={poolName} 
          onClose={() => setShowWinners(false)} 
        />
      )}
    </div>
  );
};

export default PoolCard;